import { useState } from 'react';
import {
  Button,
  ButtonIcon,
  Flex,
  Select,
  Text,
  TextField,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';
import {
  CHECK_OPERATORS,
  type CheckOperator,
  type CheckPathResolver,
} from '@internal/backstage-plugin-pulse-common';
import {
  countConditions,
  createConditionNode,
  createGroupNode,
  type ConditionNode,
  type GroupNode,
  type RuleNode,
} from '../lib/check-rule-model';
import { selectedKey, toOptions } from '../../../select-helpers';

type Option = { value: string; label: string };

/** Mirrors pointer hover onto keyboard focus so tabbing shows the same preview. */
const removalPreview = (key: string, setKey: (key: string | null) => void) => ({
  onHoverStart: () => setKey(key),
  onHoverEnd: () => setKey(null),
  onFocus: () => setKey(key),
  onBlur: () => setKey(null),
});

const borderColor = (isPending: boolean) =>
  isPending ? 'var(--bui-negative-border)' : 'var(--bui-border-1, #ccc)';

const removeGroupLabel = (conditionCount: number) => {
  if (conditionCount === 0) {
    return 'Remove this empty group';
  }
  return `Remove this group and its ${conditionCount} ${
    conditionCount === 1 ? 'condition' : 'conditions'
  }`;
};

type RuleGroupEditorProps = {
  node: GroupNode;
  onChange: (node: GroupNode) => void;
  isReadOnly: boolean;
  conditionError: boolean;
  pathResolver: CheckPathResolver;
  factRefOptions: Option[];
  onRemove?: () => void;
};

/**
 * Recursively edits an `all`/`any` group of conditions and nested groups. Each
 * group owns its combinator and its children; edits bubble up via `onChange`.
 */
export const RuleGroupEditor = ({
  node,
  onChange,
  isReadOnly,
  conditionError,
  pathResolver,
  factRefOptions,
  onRemove,
}: RuleGroupEditorProps) => {
  const [pendingRemovalKey, setPendingRemovalKey] = useState<string | null>(
    null,
  );

  const replaceChild = (index: number, child: RuleNode) =>
    onChange({
      ...node,
      children: node.children.map((existing, position) =>
        position === index ? child : existing,
      ),
    });

  const removeChild = (index: number) =>
    onChange({
      ...node,
      children: node.children.filter((_, position) => position !== index),
    });

  const addCondition = () =>
    onChange({ ...node, children: [...node.children, createConditionNode()] });

  const addGroup = () =>
    onChange({
      ...node,
      children: [
        ...node.children,
        createGroupNode('all', [createConditionNode()]),
      ],
    });

  const setCombinator = (combinator: 'all' | 'any') =>
    onChange({ ...node, combinator });

  const updateCondition = (index: number, patch: Partial<ConditionNode>) => {
    const child = node.children[index] as ConditionNode;
    replaceChild(index, { ...child, ...patch });
  };

  const isPending = (key: string) => pendingRemovalKey === key;

  return (
    <Flex direction="column" gap="3">
      <Flex align="end" gap="2">
        <Select
          label="Match"
          options={[
            { value: 'all', label: 'all conditions' },
            { value: 'any', label: 'any condition' },
          ]}
          value={node.combinator}
          isDisabled={isReadOnly}
          onChange={keys =>
            setCombinator((selectedKey(keys) as 'all' | 'any') ?? 'all')
          }
        />
      </Flex>

      <Flex
        direction="column"
        gap="3"
        pl="4"
        data-pending-removal={isPending(node.key) || undefined}
        style={{ borderLeft: `2px solid ${borderColor(isPending(node.key))}` }}
      >
        {node.children.map((child, index) =>
          child.kind === 'condition' ? (
            <Flex
              key={child.key}
              gap="2"
              align="end"
              direction="column"
              p="3"
              data-pending-removal={isPending(child.key) || undefined}
              style={{
                border: `1px solid ${borderColor(isPending(child.key))}`,
                borderRadius: 'var(--bui-radius-3)',
              }}
            >
              <Select
                label="Fact ref"
                options={factRefOptions}
                value={child.factRef}
                isInvalid={conditionError && !child.factRef.trim()}
                isDisabled={isReadOnly}
                onChange={keys =>
                  updateCondition(index, { factRef: selectedKey(keys) ?? '' })
                }
              />
              <TextField
                label="Path"
                placeholder={
                  pathResolver === 'jsonata' ? 'spec.owner' : '$.spec.owner'
                }
                value={child.path}
                onChange={path => updateCondition(index, { path })}
                isInvalid={conditionError && !child.path.trim()}
                isDisabled={isReadOnly}
              />
              <Select
                label="Operator"
                options={toOptions(CHECK_OPERATORS)}
                value={child.operator}
                isDisabled={isReadOnly}
                onChange={keys =>
                  updateCondition(index, {
                    operator: (selectedKey(keys) as CheckOperator) ?? 'equals',
                  })
                }
              />
              <TextField
                label="Value"
                value={child.value}
                onChange={value => updateCondition(index, { value })}
                isDisabled={isReadOnly}
              />
              <ButtonIcon
                icon={<RiDeleteBinLine />}
                aria-label="Remove condition"
                variant="tertiary"
                isDisabled={isReadOnly}
                onPress={() => {
                  setPendingRemovalKey(null);
                  removeChild(index);
                }}
                {...removalPreview(child.key, setPendingRemovalKey)}
              />
            </Flex>
          ) : (
            <RuleGroupEditor
              key={child.key}
              node={child}
              onChange={updated => replaceChild(index, updated)}
              isReadOnly={isReadOnly}
              conditionError={conditionError}
              pathResolver={pathResolver}
              factRefOptions={factRefOptions}
              onRemove={() => removeChild(index)}
            />
          ),
        )}

        {node.children.length === 0 ? (
          <Text variant="body-small" color="secondary">
            No conditions in this group yet.
          </Text>
        ) : null}

        <Flex gap="2" align="center" justify="between">
          <Button
            variant="secondary"
            iconStart={<RiAddLine />}
            isDisabled={isReadOnly}
            onPress={addCondition}
          >
            Add condition
          </Button>
          {onRemove ? (
            <TooltipTrigger delay={0} closeDelay={0}>
              <ButtonIcon
                icon={<RiDeleteBinLine />}
                aria-label="Remove group"
                variant="tertiary"
                isDisabled={isReadOnly}
                onPress={onRemove}
                {...removalPreview(node.key, setPendingRemovalKey)}
              />
              <Tooltip>{removeGroupLabel(countConditions(node))}</Tooltip>
            </TooltipTrigger>
          ) : null}
        </Flex>
      </Flex>

      {/* Outside the indent so it reads as adding a group at this level. */}
      <Flex>
        <Button
          variant="tertiary"
          iconStart={<RiAddLine />}
          isDisabled={isReadOnly}
          onPress={addGroup}
        >
          Add group
        </Button>
      </Flex>
    </Flex>
  );
};
